import {
  Controls,
  Gesture,
  MEDIA_KEY_SHORTCUTS,
  MediaAnnouncer,
  MediaPlayer,
  type MediaPlayerInstance,
  type MediaPlayerProps,
  MediaProvider,
  MuteButton,
  PlayButton,
  Poster,
  Spinner,
  Time,
  TimeSlider,
  VolumeSlider,
} from '@vidstack/react';
import {
  MuteIcon,
  PauseIcon,
  PlayIcon,
  VolumeHighIcon,
  VolumeLowIcon,
} from '@vidstack/react/icons';
import { type HTMLAttributes, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';

import { useInView, useIsMounted } from '@virginexperiencedays/hooks';
import { cn } from '@virginexperiencedays/tailwind';

export type VideoPlayerProps = Omit<MediaPlayerProps, 'children'> &
  HTMLAttributes<HTMLDivElement> & {
    canFullscreen?: boolean;
    posterAlt?: string;
  };

export const VideoPlayer = forwardRef<MediaPlayerInstance, VideoPlayerProps>(
  (
    { canFullscreen, className, controls, loop, poster, posterAlt = 'Video poster', src, ...rest },
    ref,
  ) => {
    const refPlayer = useRef<MediaPlayerInstance>(null);
    const refRoot = useRef<HTMLDivElement>(null);
    const refWasPaused = useRef<boolean | undefined>(true);

    const isMounted = useIsMounted();

    const onInViewToggle = (isInView: boolean) => {
      if (!refPlayer.current) return;

      if (isInView) {
        !refWasPaused.current && refPlayer.current?.play();
      } else {
        refWasPaused.current = refPlayer.current?.paused;

        refPlayer.current?.pause();
      }
    };

    useInView({
      callbackFn: onInViewToggle,
      elementRef: refRoot,
      options: { threshold: 0.1 },
    });

    useImperativeHandle(ref, () => {
      return refPlayer.current as MediaPlayerInstance;
    });

    useEffect(() => {
      if (!isMounted) return;

      const onVisibilityChange = () => {
        if (document.hidden) {
          refWasPaused.current = refPlayer.current?.paused;

          refPlayer.current?.pause();
        } else if (!refWasPaused.current) {
          // enqueue play request if not ready to play due to loading - e.g. right after page load
          refPlayer.current?.canPlayQueue.enqueue('media-play-request', () =>
            refPlayer?.current?.play(),
          );
        }
      };

      window.addEventListener('visibilitychange', onVisibilityChange);

      return () => {
        window.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }, [isMounted]);

    if (!src) return null;

    return (
      <div ref={refRoot} className={className}>
        <MediaPlayer
          ref={refPlayer}
          className="group/player h-full w-full border-none [&_video]:size-full [&_video]:object-cover"
          style={{ '--media-poster-bg': 'transparent' }}
          keyShortcuts={{
            ...MEDIA_KEY_SHORTCUTS,
            toggleFullscreen: canFullscreen ? 'f' : undefined,
          }}
          loop={loop}
          src={src}
          playsInline
          {...rest}
        >
          <MediaProvider>
            {!!poster?.length && (
              <Poster
                className="vds-poster h-full object-cover transition-opacity data-[error]:opacity-0 data-[hidden]:opacity-0 data-[loading]:opacity-0"
                src={poster}
                alt={posterAlt}
              />
            )}
          </MediaProvider>

          <MediaAnnouncer />
          <VideoPlayerBufferingIndicator />

          {/* some controls (mainly buttons) components aren't SSR friendly - hydration issues */}
          {isMounted && <VideoPlayerControls isSimple={!controls} />}

          {controls && (
            <Gesture
              className="absolute left-0 top-0 h-full w-full"
              event="click"
              action="toggle:paused"
            />
          )}
        </MediaPlayer>
      </div>
    );
  },
);

VideoPlayer.displayName = 'VideoPlayer';

type VideoPlayerControlsProps = {
  isSimple?: boolean;
};

export const VideoPlayerControls = ({ isSimple }: VideoPlayerControlsProps) => {
  return (
    <Controls.Root className="duration-250 pointer-events-none absolute inset-0 z-10 flex h-full w-full flex-col justify-end bg-gradient-to-t from-black/10 to-transparent text-white">
      <Controls.Group
        className={cn(
          'pointer-events-auto flex w-full items-center px-2',
          isSimple && 'pb-2 lg:pb-4 lg:pl-4',
        )}
      >
        {isSimple ? (
          <VideoPlayerSimplePlayButton />
        ) : (
          <>
            <VideoPlayerPlayButton />
            <VideoPlayerTimeSlider />
            <VideoPlayerSoundButton />
          </>
        )}
      </Controls.Group>
    </Controls.Root>
  );
};

export const VideoPlayerBufferingIndicator = () => {
  return (
    <div className="pointer-events-none absolute inset-0 z-50 flex h-full w-full items-center justify-center">
      <Spinner.Root
        className="media-buffering:animate-spin media-buffering:opacity-100 text-white opacity-0 transition-opacity duration-200 ease-linear"
        size={84}
      >
        <Spinner.Track className="opacity-25" width={8} />
        <Spinner.TrackFill className="opacity-75" width={8} />
      </Spinner.Root>
    </div>
  );
};

export const VideoPlayerSimplePlayButton = () => {
  return (
    <PlayButton
      className="text-neutral bg-background-page-faded border-border-neutral hover-only:hover:bg-background-neutral-strong hover-only:hover:border-background-page-faded hover-only:hover:text-background-page-faded duration-250 group relative inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-solid opacity-0 outline-none transition-all ease-in-out before:absolute before:-inset-[9px] before:content-[''] group-data-[can-play]/player:opacity-100 lg:h-6 lg:w-6 lg:before:-inset-4"
      onClick={(e) => e.stopPropagation()}
    >
      <PlayIcon className="hidden h-5 w-5 group-data-[paused]:block lg:h-4 lg:w-4" />
      <PauseIcon className="h-5 w-5 group-data-[paused]:hidden lg:h-4 lg:w-4" />
    </PlayButton>
  );
};

export const VideoPlayerPlayButton = () => {
  return (
    <PlayButton className="group relative inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none ring-inset ring-sky-400 hover:bg-white/20 data-[focus]:ring-4">
      <PlayIcon className="hidden size-6 group-data-[paused]:block" />
      <PauseIcon className="size-6 group-data-[paused]:hidden" />
    </PlayButton>
  );
};

export const VideoPlayerTimeSlider = () => {
  return (
    <TimeSlider.Root
      className="group/video-time relative mx-[7.5px] inline-flex h-10 w-full cursor-pointer touch-none select-none items-center outline-none aria-hidden:hidden"
      noSwipeGesture
    >
      <TimeSlider.Track className="relative z-0 h-[5px] w-full rounded-sm bg-white/30 ring-sky-400 group-data-[focus]/video-time:ring-[3px]">
        <TimeSlider.TrackFill className="bg-background-primary absolute h-full w-[var(--slider-fill)] rounded-sm will-change-[width]" />
        <TimeSlider.Progress className="absolute z-10 h-full w-[var(--slider-progress)] rounded-sm bg-white/50 will-change-[width]" />
      </TimeSlider.Track>

      <TimeSlider.Thumb className="absolute left-[var(--slider-fill)] top-1/2 z-20 h-[15px] w-[15px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-white bg-white opacity-0 ring-white/40 transition-opacity will-change-[left] group-data-[active]/video-time:opacity-100 group-data-[dragging]/video-time:ring-4" />

      <TimeSlider.Preview className="vds-slider-preview flex gap-x-1 text-xs opacity-0 [text-shadow:_0_1px_0_rgb(0_0_0_/_40%)] group-data-[active]/video-time:opacity-100">
        <TimeSlider.Value /> / <Time type="duration" />
      </TimeSlider.Preview>
    </TimeSlider.Root>
  );
};

export const VideoPlayerSoundButton = () => {
  return (
    <div className="relative shrink-0">
      <MuteButton className="group peer relative inline-flex size-8 cursor-pointer items-center justify-center rounded-full outline-none ring-inset ring-sky-400 hover:bg-white/20 data-[focus]:ring-4">
        <MuteIcon className="hidden size-7 group-data-[state='muted']:block" />
        <VolumeLowIcon className="hidden size-7 group-data-[state='low']:block" />
        <VolumeHighIcon className="hidden size-7 group-data-[state='high']:block" />
      </MuteButton>

      <VideoPlayerVolumeSlider />
    </div>
  );
};

export const VideoPlayerVolumeSlider = () => {
  return (
    // coarse-hover doesn't work on Samsung devices due to their known bug, so we add additionally hide the slider below lg breakpoint
    <div className="coarse-hover:hidden invisible absolute bottom-full left-0 hidden h-[90px] w-full items-center justify-center opacity-0 hover:visible hover:opacity-100 peer-[[data-hocus]]:visible peer-[[data-hocus]]:opacity-100 aria-hidden:hidden lg:flex">
      <VolumeSlider.Root
        className="group/video-volume relative flex h-[70px] cursor-pointer touch-none select-none items-center justify-center outline-none"
        orientation="vertical"
      >
        <VolumeSlider.Track className="relative z-0 h-full w-[5px] rounded-sm bg-white/30 ring-sky-400 group-data-[focus]/video-volume:ring-[3px]">
          <VolumeSlider.TrackFill className="bg-background-primary absolute bottom-0 h-[var(--slider-fill)] w-full rounded-sm will-change-[height]" />
        </VolumeSlider.Track>

        <VolumeSlider.Thumb className="absolute bottom-[var(--slider-fill)] left-1/2 z-20 h-[15px] w-[15px] -translate-x-1/2 translate-y-1/2 rounded-full border border-white bg-white ring-white/40 transition-opacity will-change-[bottom] group-data-[dragging]/video-volume:ring-4" />
      </VolumeSlider.Root>
    </div>
  );
};
